import type { TypeEmailPromoModal } from "./generated/TypeEmailPromoModal";

/**
 * The optional `trigger` strings are hardcoded in Contentful, so the
 * table below is the contract between the CMS enum and app behavior. It is
 * keyed by the generated union, so adding a tier to the content model breaks
 * `tsc` here until the new value is mapped.
 */
export type TriggerValue = NonNullable<TypeEmailPromoModal<undefined, string>["fields"]["trigger"]>;

/** What the trigger does. The table holds these; `parseTrigger` adds the label it resolved. */
export type TriggerBehavior = { kind: "delay"; delayMs: number } | { kind: "exit" };

/** Used when an editor leaves both trigger fields blank: an active modal still shows, after 5s. */
export const DEFAULT_TRIGGER: TriggerValue = "Delay: 5 sec";

export const TRIGGER_TABLE: Record<TriggerValue, TriggerBehavior> = {
  "Delay: 0 sec": { kind: "delay", delayMs: 0 },
  "Delay: 3 sec": { kind: "delay", delayMs: 3000 },
  "Delay: 5 sec": { kind: "delay", delayMs: 5000 },
  "Delay: 10 sec": { kind: "delay", delayMs: 10000 },
  "Delay: 30 sec": { kind: "delay", delayMs: 30000 },
  "On exit": { kind: "exit" },
};

/** Reported for entries still on the legacy free-form delay, which has no editor-facing tier. */
export const LEGACY_TRIGGER_LABEL = "Legacy delay";

/** "Popup Displayed" reports as `triggerType`. */
export type TriggerLabel = TriggerValue | typeof LEGACY_TRIGGER_LABEL;

/** The behavior plus the label that produced it — one resolution, so the two can't disagree. */
export type ParsedTrigger = TriggerBehavior & { label: TriggerLabel };

export interface TriggerFields {
  trigger?: TriggerValue;
  delayInSeconds?: number; // Legacy free-form delay, superseded by `trigger`.
}

/**
 * Single entry point for reading the trigger: the `trigger` enum, then legacy `delayInSeconds`,
 * then the default. An enum value missing from the table falls through, so a model edited without
 * re-running `pnpm types:contentful` still resolves.
 *
 * `label` ("Popup Displayed" reports it) rides along rather than taking a second pass over the
 * same fields, so it can't disagree with the behavior it describes.
 */
export function parseTrigger({ trigger, delayInSeconds }: TriggerFields): ParsedTrigger {
  if (trigger) {
    const fromTable = TRIGGER_TABLE[trigger];
    if (fromTable) return { ...fromTable, label: trigger };
  }

  if (typeof delayInSeconds === "number" && Number.isFinite(delayInSeconds) && delayInSeconds >= 0) {
    return { kind: "delay", delayMs: Math.round(delayInSeconds * 1000), label: LEGACY_TRIGGER_LABEL };
  }

  return { ...TRIGGER_TABLE[DEFAULT_TRIGGER], label: DEFAULT_TRIGGER };
}
